import { useMemo } from 'react';

import { Types } from '@scorenco/core';
import { motion, AnimatePresence } from 'framer-motion';

import { ClubTitle } from '../../screens/ClubTitle';
import { Facebook } from '../../screens/Facebook';
import { IncomingEvents } from '../../screens/IncomingEvents';
import { Instagram } from '../../screens/Instagram';
import { Partners } from '../../screens/Partners';
import { ShortNews } from '../../screens/ShortNews';
import { Team } from '../../screens/Team';
import { TournamentAdScorenco } from '../../screens/TournamentAdScorenco';
import { TournamentChallengeRanking } from '../../screens/TournamentChallengeRanking';
import { TournamentEliminationPhase } from '../../screens/TournamentEliminationPhase';
import { TournamentLives } from '../../screens/TournamentLives';
import { TournamentPhase } from '../../screens/TournamentPhase';

import { useScreenViewerContext } from './ScreenViewerContext';

const SCREENS_CONFIG = {
  clubtitle: ClubTitle,
  team: Team,
  brief: ShortNews,
  incomingEvents: IncomingEvents,
  sponsors: Partners,
  fbSocialWall: Facebook,
  tournamentPhase: TournamentPhase,
  tournamentLives: TournamentLives,
  tournamentAdScorenco: TournamentAdScorenco,
  tournamentChallengeRanking: TournamentChallengeRanking,
  tournamentEliminationPhase: TournamentEliminationPhase,
  instagram: Instagram,
};

type ScreenSlug = keyof typeof SCREENS_CONFIG;

const isScreenSlug = (slug: string): slug is ScreenSlug =>
  slug in SCREENS_CONFIG;

export const ScreenView = ({
  clubHouse,
}: {
  clubHouse: Types.Clubhouse_Club_House;
}) => {
  const { current, page } = useScreenViewerContext();
  const screenSlug = current?.template?.slug;

  const ScreenContent = useMemo(() => {
    if (!screenSlug || !isScreenSlug(screenSlug)) {
      return null;
    }

    return SCREENS_CONFIG[screenSlug];
  }, [screenSlug]);

  if (!ScreenContent || !current) {
    return null;
  }

  return (
    <AnimatePresence initial={false}>
      <motion.div
        key={page}
        initial={{ x: '100%', opacity: 0 }}
        animate={{
          x: 0,
          opacity: 1,
          zIndex: screenSlug === 'clubtitle' ? 10 : 1,
        }}
        exit={{ x: '-100%', opacity: 0, zIndex: 0 }}
        style={{
          position: 'absolute',
          width: '100%',
        }}
        transition={{
          x: { type: 'spring', stiffness: 300, damping: 30 },
          opacity: { duration: 0.2 },
        }}
      >
        <ScreenContent screen={current} clubHouse={clubHouse} />
      </motion.div>
    </AnimatePresence>
  );
};
